import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { Address } from "@blaze-cardano/core";

import { GummiwormContext } from "./context";
import type { IGummiwormState } from "./types";
import { useDepositTxs } from "./hooks/useDepositTxs";
import type { IDepositHistoryTx } from "./hooks/useDepositTxs";
import { useWithdrawTxs } from "./hooks/useWithdrawTxs";
import { useAppContext } from "../AppContext";
import { Core } from "@blaze-cardano/sdk";
import { useWebSocket, type WsAuthSigner } from "@/hooks/useWebSocket";
import { ensureGuestWsIdentity } from "@/lib/guestIdentity";
import { signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { useReDelegateKey } from "@/hooks/useReDelegateKey";
import { useDelegatedKeyStatus } from "@/hooks/useDelegatedKeyStatus";
import {
  needsRequestPolling,
  polledStatusUpdate,
  rejectionUpdate,
  shouldRequestSignature,
  shouldSubmitDeposit,
  shouldSubmitRefund,
  depositTxExpiryMs,
  depositTxCanStillLand,
} from "@/lib/depositLifecycle";

export const MOCK_USER_ACCOUNT_ID = 1n;

// Reconciler cadences. Inline literals, not __APP_CONFIG: that carries
// per-deployment wiring (URLs), whereas these are operational timings that do
// not vary between deployments.
/** How long to wait for an L1 tx (deposit or refund) to confirm on Cardano. */
const L1_CONFIRMATION_TIMEOUT_MS = 120_000;
/** Poll interval for hydrozoa's per-request status (hard confirmation, refund tx). */
const REQUEST_POLL_INTERVAL_MS = 10_000;
/** Re-check interval for post-dated refunds waiting to become valid. */
const REFUND_RECHECK_INTERVAL_MS = 15_000;

export const GummiwormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { blaze, provider, orderEncryptionKey },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx, clearDepositTxs } = useDepositTxs();
  const { withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs } = useWithdrawTxs();
  const paymentPartHash = useMemo(() => {
    if (!mainAddress) return null;
    return Address.fromBech32(mainAddress).getProps().paymentPart?.hash ?? null;
  }, [mainAddress]);
  // Market data (order book, candles, tradingEnabled) is only ever pushed over this
  // per-connection channel, so it must open even without a wallet. A guest identity
  // never matches a real deposit account, so myAccountId/ledgerView.account (below)
  // stay null and all wallet-owner gating elsewhere is unaffected.
  const wsAccountHash = useMemo(
    () => paymentPartHash ?? ensureGuestWsIdentity(),
    [paymentPartHash],
  );
  const wsUrl = `${window.__APP_CONFIG.apiWsUrl}/ws`;

  // Sign the WS private-subscribe auth with the local ephemeral (delegated)
  // key, so account/orders/deposits need no wallet prompt. Undefined for a
  // guest -- guests own no private data and skip those channels.
  const signAuth = useMemo<WsAuthSigner | undefined>(() => {
    if (!mainAddress || !provider) return undefined;
    return async (message: string) => {
      const { key, signature } = await signWithEphemeralWallet(
        provider,
        mainAddress,
        Buffer.from(message, "utf8"),
      );
      return { coseKey: key, coseSignature: signature };
    };
  }, [mainAddress, provider]);

  // The delegated key that lets this browser read the account's private
  // channels. The server says which of the two situations we are in
  // (`account_not_found` -- nothing to sign for yet -- versus
  // `key_not_registered`), so nothing here infers it from local deposit
  // history, and nothing prompts the wallet on its own: the user presses a
  // button in DelegatedKeyCard. See useDelegatedKeyStatus.
  const reDelegate = useReDelegateKey();
  const accountNonceRef = useRef(0);
  const registerDelegatedKey = useCallback(
    () => reDelegate(accountNonceRef.current),
    [reDelegate],
  );
  const delegatedKey = useDelegatedKeyStatus(
    // Signing needs the wallet and the order-encryption context; gating the
    // button on them is what removes the old race, where an auto-prompt fired
    // before the handshake finished and silently did nothing.
    Boolean(mainAddress && orderEncryptionKey),
    registerDelegatedKey,
  );
  const onAuthError = delegatedKey.onAuthError;

  // Reads use the plaintext channel protocol (no encryption); writes still use
  // `orderEncryptionKey` on the HTTP submit path, so it stays in context above.
  const { ledgerView, connected: wsConnected, error: wsError, send: sendWsMessage, watchMarket, loadMoreOrders } = useWebSocket(wsUrl, wsAccountHash, signAuth, onAuthError, delegatedKey.onAuthSuccess);
  // Keep the recovery nonce base current without re-subscribing the socket.
  accountNonceRef.current = ledgerView.account ? Number(ledgerView.account.nonce) : 0;
  const markets = useMemo(() => Object.values(ledgerView.markets) as any[], [ledgerView.markets]);
  const accountsReady = wsConnected;

  const tradingEnabled = useMemo(() => {
    if (!ledgerView) return false;
    return ledgerView.tradingEnabled;
  }, [ledgerView]);

  const competitionStartMs = useMemo(
    () => ledgerView?.competitionStartMs ?? null,
    [ledgerView],
  );

  const maintenanceMode = useMemo(() => !!ledgerView?.maintenanceMode, [ledgerView]);
  const maintenanceUntilMs = useMemo(
    () => ledgerView?.maintenanceUntilMs ?? null,
    [ledgerView],
  );

  const myAccountId = useMemo(() => {
    if (!paymentPartHash) return null;
    // The account id is the payment key hash as a hex string, matching the wire
    // (Ed25519KeyHash serializes as hex) — no u256 conversion.
    return paymentPartHash;
  }, [paymentPartHash]);

  useEffect(() => {
    for (let deposit of Object.values(ledgerView.deposits)) {
      if (deposit.absorbedInBlock !== 0n) {
        if (depositTxs.has(deposit.requestId.toString())) {
          updateDepositTx(deposit.requestId, { status: "absorbed" });
        }
        continue;
      }
      if (deposit.rejectedInBlock !== 0n) {
        // Hash-aware: a deposit rejected before its L1 tx was ever submitted
        // has no UTxO to refund, so it must not enter the refund flow.
        const existing = depositTxs.get(deposit.requestId.toString());
        updateDepositTx(
          deposit.requestId,
          rejectionUpdate(existing ?? { hash: "" }, deposit.failureReason),
        );
        continue;
      }
      if (deposit.refundTx) {
        // refundTx is only ever set by the BlockConfirmed event, so its
        // presence in the ledger view IS the hard-confirmation signal, and
        // this copy of the refund is the multisigned one.
        const existing = depositTxs.get(deposit.requestId.toString());
        if (existing) {
          const refundCbor = Buffer.from(deposit.refundTx).toString("hex");
          let refundTxHash = existing.refundTxHash;
          if (!refundTxHash) {
            try {
              refundTxHash = Core.Transaction.fromCbor(
                Core.TxCBOR(refundCbor),
              ).getId();
            } catch (err) {
              console.error("Failed to compute refund tx hash:", err);
            }
          }
          // Never downgrade a status that has already progressed past
          // confirmation (settling, on-chain, refund flow, ...): only
          // promote new/pending, otherwise leave status untouched.
          const update: Partial<IDepositHistoryTx> = {
            refundCbor,
            refundTxHash,
            hardConfirmed: true,
          };
          if (["new", "pending"].includes(existing.status)) {
            update.status = "confirmed";
          }
          updateDepositTx(deposit.requestId, update);
        }
        continue;
      }
      if (!depositTxs.has(deposit.requestId.toString())) {
        addDepositTx(deposit.requestId, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "pending",
        });
      } else {
        updateDepositTx(deposit.requestId, { status: "pending" });
      }
    }
  }, [ledgerView.deposits]);

  // ── Refund tx poller ────────────────────────────────────────────────
  // Hydrozoa mints a post-dated refund tx for every registered deposit as
  // a safety valve, and no longer pushes it to us via the ledger event log
  // (sugar-rush stopped tailing hydrozoa's rocksdb directly). Poll hydrozoa's
  // HTTP API for each deposit that doesn't have one yet.
  const pollingRefunds = useRef(new Set<string>());
  const depositTxsRef = useRef(depositTxs);
  useEffect(() => {
    depositTxsRef.current = depositTxs;
  }, [depositTxs]);

  useEffect(() => {
    const pollOnce = async () => {
      const pending = [...depositTxsRef.current.entries()].filter(([, tx]) =>
        needsRequestPolling(tx),
      );
      if (pending.length === 0) return;

      await Promise.all(
        pending.map(async ([id]) => {
          if (pollingRefunds.current.has(id)) return;
          pollingRefunds.current.add(id);
          try {
            const response = await fetch(
              `${window.__APP_CONFIG.apiBaseUrl}/head/requests/${id}`,
            );
            if (!response.ok) return;
            const data = await response.json();
            const update: Partial<IDepositHistoryTx> = {};

            // Hard confirmation: hydrozoa's request status is the
            // authoritative source (the ws BlockConfirmed path is dead until
            // the wire contract carries ProxyBlockConfirmation — see
            // depositLifecycle.ts).
            const current = depositTxsRef.current.get(id);
            if (current) {
              Object.assign(update, polledStatusUpdate(current, data) ?? {});
            }

            const refund = data.status?.relatedEffects?.find(
              (e: any) => e.kind == "refund",
            );
            if (refund && !current?.refundCbor) {
              const refundTxId = refund.l1TxId;
              const getRefundBody = await fetch(
                `${window.__APP_CONFIG.apiBaseUrl}/head/effects/${refundTxId}`,
              );
              if (getRefundBody.ok) {
                const effectData = await getRefundBody.json();
                if (effectData.txCbor) {
                  update.refundCbor = effectData.txCbor;
                  update.refundTxHash = refundTxId;
                }
              }
            }

            if (Object.keys(update).length > 0) {
              updateDepositTx(BigInt(id), update);
            }
          } catch (err) {
            console.error("Error polling hydrozoa deposit status:", err);
          } finally {
            pollingRefunds.current.delete(id);
          }
        }),
      );
    };

    pollOnce();
    const interval = window.setInterval(pollOnce, REQUEST_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [updateDepositTx]);

  // ── Deposit reconciler ──────────────────────────────────────────────
  // Single place that handles L1 submission and confirmation monitoring.
  // Runs whenever depositTxs or blaze changes. Uses a ref to track which
  // deposits are already being handled so we don't double-submit.
  const handling = useRef(new Set<string>());

  useEffect(() => {
    if (!blaze || !blaze.provider) return;

    for (const [id, tx] of depositTxs.entries()) {
      if (handling.current.has(id)) continue;

      // Fallback only: deposits normally sign at click time in
      // useHandleDepositRequest. Entries persisted by builds that deferred
      // the signature can still hold an unsigned tx — prompt for those.
      if (shouldRequestSignature(tx)) {
        handling.current.add(id);
        (async () => {
          try {
            const unsigned = Core.Transaction.fromCbor(
              Core.TxCBOR(tx.unsignedCbor!),
            );
            const signed = await blaze.signTransaction(unsigned);
            updateDepositTx(BigInt(id), { cbor: signed.toCbor() });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            updateDepositTx(BigInt(id), {
              status: "failed",
              errorMessage: `Deposit was not signed: ${msg}`,
            });
          } finally {
            handling.current.delete(id);
          }
        })();
        continue;
      }

      // Ready to submit: signed, refund tx in hand, AND the deposit's L2
      // block is hard confirmed (see shouldSubmitDeposit) — never earlier.
      if (shouldSubmitDeposit(tx)) {
        handling.current.add(id);
        (async () => {
          const signedTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.cbor));
          // The id is a hash of the signed tx, so we know it before submitting.
          // That matters: a wallet can broadcast and *then* throw (Eternl does
          // this when the selected account changes mid-flow), and a thrown
          // error is therefore not evidence the tx failed to reach the chain.
          // Record where to look first, then submit; the confirmation watcher
          // below decides the outcome from the chain rather than from the throw.
          const hash = signedTx.getId().toString();
          updateDepositTx(BigInt(id), {
            status: "settling",
            hash,
            submitAttempts: (tx.submitAttempts ?? 0) + 1,
          });
          try {
            await blaze.submitTransaction(signedTx);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes("already submitted") || msg.includes("AlreadyExists")) {
              // Already on its way — nothing to do.
            } else {
              // Deliberately not terminal. Leave it settling so the watcher
              // checks the chain; if the tx really never landed, the
              // confirmation timeout retries or fails it there.
              console.warn(`deposit ${id}: submit threw, awaiting chain: ${msg}`);
            }
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Settling: monitor for L1 confirmation
      if (tx.status === "settling" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, L1_CONFIRMATION_TIMEOUT_MS)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "on-chain" });
              return;
            }
            // Not seen yet. The tx's own TTL is the only bound that matters:
            // until it passes the tx can still be included, so keep trying;
            // after it passes no amount of retrying can help.
            const expiryMs = depositTxExpiryMs(tx.cbor, (slot) =>
              blaze.provider.slotToUnix(slot),
            );
            if (depositTxCanStillLand(expiryMs, Date.now())) {
              // Clearing the hash puts it back in front of shouldSubmitDeposit,
              // which re-submits the same signed bytes -- no re-signing, so no
              // new wallet prompt. The id is deterministic, so if it did land
              // after all the duplicate is rejected harmlessly and the next
              // confirmation check sees it on chain.
              updateDepositTx(BigInt(id), { status: "confirmed", hash: "" });
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage:
                  "Deposit transaction expired before it reached the chain.",
              });
            }
          })
          .catch((err) => {
            console.error("Error monitoring L1 tx confirmation:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }

    }
  }, [depositTxs, blaze, updateDepositTx]);

  // ── Refund reconciler ───────────────────────────────────────────────
  // A deposit the network rejected *after* its L1 tx was submitted (status
  // "rejected"; one rejected before reaching L1 is "failed" and never lands
  // here) is made whole by submitting hydrozoa's post-dated refund tx, which
  // spends the deposit UTxO back to the wallet. The refund carries a
  // validity-start interval, so it is only submittable once that slot has
  // passed — re-check on an interval until each pending refund becomes valid.
  const refunding = useRef(new Set<string>());

  useEffect(() => {
    if (!blaze || !blaze.provider) return;
    const provider = blaze.provider;

    // True once the refund tx's validity-start slot has passed. Hydrozoa
    // post-dates the refund; submitting earlier is rejected by the node.
    const refundIsValid = (refundCbor: string): boolean => {
      try {
        const validFrom = Core.Transaction.fromCbor(Core.TxCBOR(refundCbor))
          .body()
          .validityStartInterval();
        if (validFrom == null) return true;
        return Number(provider.unixToSlot(Date.now())) >= Number(validFrom);
      } catch (err) {
        console.error("Failed to read refund validity interval:", err);
        return false;
      }
    };

    const monitorRefund = (id: string, hash: string) => {
      provider
        .awaitTransactionConfirmation(hash, L1_CONFIRMATION_TIMEOUT_MS)
        .then((confirmed) => {
          if (confirmed) updateDepositTx(BigInt(id), { status: "refunded" });
        })
        .catch((err) => console.error("Error monitoring refund tx:", err))
        .finally(() => refunding.current.delete(id));
    };

    const reconcileRefunds = () => {
      for (const [id, tx] of depositTxsRef.current.entries()) {
        if (refunding.current.has(id)) continue;

        // Resume monitoring a refund submitted before a reload.
        if (tx.status === "refunding" && tx.hash) {
          refunding.current.add(id);
          monitorRefund(id, tx.hash);
          continue;
        }

        if (!shouldSubmitRefund(tx, refundIsValid(tx.refundCbor))) continue;

        refunding.current.add(id);
        (async () => {
          try {
            const refundTx = Core.Transaction.fromCbor(
              Core.TxCBOR(tx.refundCbor),
            );
            const refundHash = await blaze.submitTransaction(refundTx);
            // hash drives the "refunding" monitor path; refundTxHash already
            // holds the multisigned refund id for display/download.
            updateDepositTx(BigInt(id), {
              hash: refundHash,
              status: "refunding",
            });
            monitorRefund(id, refundHash);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (
              (msg.includes("already submitted") ||
                msg.includes("AlreadyExists")) &&
              tx.refundTxHash
            ) {
              // Refund already on-chain (e.g. resubmit after a reload): the
              // multisigned refund id IS its L1 hash — monitor to confirmation.
              updateDepositTx(BigInt(id), {
                hash: tx.refundTxHash,
                status: "refunding",
              });
              monitorRefund(id, tx.refundTxHash);
            } else {
              // Leave status "rejected" so the next tick retries (transient
              // node/network failure, or the refund is not yet valid).
              console.error("Error submitting refund tx:", err);
              refunding.current.delete(id);
            }
          }
        })();
      }
    };

    reconcileRefunds();
    const interval = window.setInterval(reconcileRefunds, REFUND_RECHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [depositTxs, blaze, updateDepositTx]);

  const contextValue = useMemo<IGummiwormState>(
    () => ({
      accountsReady,
      markets,
      myAccountId,
      error: wsError,
      tradingEnabled,
      competitionStartMs,
      maintenanceMode,
      maintenanceUntilMs,
      loadMoreOrders,
      depositTxs,
      clearDepositTxs,
      withdrawTxs,
      addWithdrawTx,
      updateWithdrawTx,
      clearWithdrawTxs,
      sendWsMessage,
      watchMarket,
      ledgerView,
      delegatedKey: delegatedKey.state,
      delegatedKeyCanSign: delegatedKey.canSign,
      signDelegatedKey: delegatedKey.signAndRegister,
      dismissDelegatedKeySigning: delegatedKey.dismiss,
    }),
    [wsConnected, wsError, markets, myAccountId, tradingEnabled, delegatedKey.state, delegatedKey.canSign, delegatedKey.signAndRegister, delegatedKey.dismiss, depositTxs, clearDepositTxs, withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs, sendWsMessage, watchMarket, ledgerView],
  );

  return (
    <GummiwormContext.Provider value={contextValue}>
      {children}
    </GummiwormContext.Provider>
  );
};
